import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Upload, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import PageHeader from './PageHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

/**
 * Generic per-user CRUD manager. Config-driven so each "record" feature is a thin wrapper.
 */
export default function RecordManager({
  apiPath, title, subtitle, icon: Icon, accent = 'navy',
  fields = [], titleField, subtitleFields = [], hasPhoto = false, hasDocuments = false,
  emptyText = 'Nothing here yet.', addLabel = 'Add', user, testid = 'records',
}) {
  const token = localStorage.getItem('session_token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [photo, setPhoto] = useState('');
  const [docs, setDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const photoInput = useRef(null);
  const docsInput = useRef(null);

  const accentBg = accent === 'gold' ? 'bg-[#C8A97E]' : 'bg-[#2B4C7E]';
  const accentText = accent === 'gold' ? 'text-[#C8A97E]' : 'text-[#2B4C7E]';
  const accentBtn = accent === 'gold'
    ? 'bg-[#C8A97E] hover:bg-[#B3956B]' : 'bg-[#2B4C7E] hover:bg-[#1E3A63]';
  const accentTile = accent === 'gold' ? 'bg-[#F7F1E8]' : 'bg-[#EBF0F5]';

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/${apiPath}`, authHeader);
      setItems(res.data || []);
    } catch {
      toast.error('Could not load your records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, authHeader).then((r) => setMe(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const openAdd = () => {
    setEditing(null); setForm({}); setPhoto(''); setDocs([]); setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    setPhoto(item.photo || '');
    setDocs(item.documents || []);
    setOpen(true);
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(await fileToDataUrl(f));
  };
  const onDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    const added = await Promise.all(files.map(async (f) => ({ name: f.name, data: await fileToDataUrl(f) })));
    setDocs((prev) => [...prev, ...added]);
  };

  const save = async () => {
    if (titleField && !form[titleField]) {
      toast.error('Please fill in the required name/title.');
      return;
    }
    setSaving(true);
    const payload = { ...form };
    if (hasPhoto) payload.photo = photo;
    if (hasDocuments) payload.documents = docs;
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/${apiPath}/${editing.id}`, payload, authHeader);
        toast.success('Saved.');
      } else {
        await axios.post(`${BACKEND_URL}/api/${apiPath}`, payload, authHeader);
        toast.success('Added.');
      }
      setOpen(false);
      load();
    } catch {
      toast.error('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/${apiPath}/${item.id}`, authHeader);
      toast.success('Deleted.');
      load();
    } catch {
      toast.error('Could not delete.');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid={`${testid}-page`}>
      <PageHeader
        title={title}
        user={me}
        titleIcon={<div className={`w-9 h-9 rounded-xl ${accentTile} flex items-center justify-center`}><Icon className={`h-5 w-5 ${accentText}`} /></div>}
        actions={
          <Button onClick={openAdd} data-testid={`${testid}-add-button`} className={`rounded-full ${accentBtn} text-white text-xs sm:text-sm`}>
            <Plus className="h-4 w-4 mr-1" /> {addLabel}
          </Button>
        }
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <p className="text-slate-500 mb-6">{subtitle}</p>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className={`h-8 w-8 animate-spin ${accentText}`} /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className={`w-20 h-20 rounded-3xl ${accentTile} flex items-center justify-center mx-auto mb-5`}>
              <Icon className={`h-10 w-10 ${accentText}`} />
            </div>
            <p className="text-slate-500 mb-6">{emptyText}</p>
            <Button onClick={openAdd} className={`rounded-full ${accentBtn} text-white`}>
              <Plus className="h-4 w-4 mr-1" /> {addLabel}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {items.map((item) => (
                <motion.div key={item.id} layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}>
                  <Card className="rounded-3xl border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] h-full" data-testid={`${testid}-card`}>
                    {hasPhoto && item.photo && (
                      <img src={item.photo} alt={item[titleField]} className="w-full h-44 object-cover" />
                    )}
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-display text-lg font-bold text-[#1C252E]">{item[titleField] || 'Untitled'}</h3>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => openEdit(item)} data-testid={`${testid}-edit`} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => remove(item)} data-testid={`${testid}-delete`} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                      <div className="mt-2 space-y-1">
                        {subtitleFields.map((fn) => {
                          const f = fields.find((x) => x.name === fn);
                          if (!item[fn]) return null;
                          return (
                            <p key={fn} className="text-sm text-slate-600">
                              <span className="text-slate-400">{f?.label || fn}: </span>{item[fn]}
                            </p>
                          );
                        })}
                      </div>
                      {hasDocuments && item.documents?.length > 0 && (
                        <div className={`mt-3 inline-flex items-center gap-1.5 text-xs ${accentText} ${accentTile} px-2.5 py-1 rounded-full`}>
                          <FileText className="h-3.5 w-3.5" /> {item.documents.length} document{item.documents.length > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1C252E]">{editing ? 'Edit' : addLabel}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((f) => (
              <div key={f.name}>
                <label className="text-sm font-medium text-slate-700 mb-1 block">{f.label}</label>
                {f.type === 'textarea' ? (
                  <Textarea
                    data-testid={`${testid}-field-${f.name}`}
                    value={form[f.name] || ''}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    placeholder={f.placeholder}
                    className="rounded-xl border-slate-200 min-h-[90px]"
                  />
                ) : f.type === 'select' ? (
                  <select
                    data-testid={`${testid}-field-${f.name}`}
                    value={form[f.name] || ''}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-slate-700 focus:border-[#2B4C7E] focus:outline-none"
                  >
                    <option value="">Select…</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input
                    data-testid={`${testid}-field-${f.name}`}
                    type={f.type || 'text'}
                    value={form[f.name] || ''}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    placeholder={f.placeholder}
                    className="rounded-xl border-slate-200 h-11"
                  />
                )}
              </div>
            ))}

            {hasPhoto && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Photo</label>
                {photo ? (
                  <div className="relative inline-block">
                    <img src={photo} alt="preview" className="h-28 w-28 object-cover rounded-2xl" />
                    <button onClick={() => setPhoto('')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <button onClick={() => photoInput.current?.click()} className="flex items-center gap-2 text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-2xl px-4 py-3 hover:border-[#2B4C7E]">
                    <ImageIcon className="h-4 w-4" /> Upload a photo
                  </button>
                )}
                <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid={`${testid}-photo-input`} />
              </div>
            )}

            {hasDocuments && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Documents</label>
                <div className="space-y-2">
                  {docs.map((d, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2 text-sm">
                      <span className="flex items-center gap-2 text-slate-600 truncate"><FileText className="h-4 w-4 shrink-0" /> {d.name}</span>
                      <button onClick={() => setDocs(docs.filter((_, j) => j !== i))} className="text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <button onClick={() => docsInput.current?.click()} className="flex items-center gap-2 text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-2xl px-4 py-3 hover:border-[#2B4C7E] w-full justify-center">
                    <Upload className="h-4 w-4" /> Add documents
                  </button>
                </div>
                <input ref={docsInput} type="file" multiple className="hidden" onChange={onDocs} data-testid={`${testid}-docs-input`} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`${testid}-save-button`} className={`rounded-full ${accentBtn} text-white`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save Changes' : addLabel)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
